import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import WhatsAppCta from "@/components/WhatsAppCta";
import { useAuth } from "@/context/AuthContext";

/**
 * Welcome screen shown right after onboarding completes.
 * Gives the trader an explicit choice between WhatsApp and Web —
 * which is the *whole point* of Option A (WhatsApp-first, web also).
 */
export default function Welcome() {
  const { user } = useAuth();
  const firstName = (user?.owner_name || "").split(" ")[0] || "there";

  return (
    <div className="min-h-[100dvh] bg-kudi-cream flex items-center justify-center px-5 py-10" data-testid="welcome-screen">
      <div className="w-full max-w-3xl">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5">
            <CheckCircle2 className="w-3.5 h-3.5" /> You&apos;re all set
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Welcome to Kobolog, {firstName}! 🎉
          </h1>
          <p className="mt-3 text-kudi-ink/65 max-w-xl mx-auto">
            Your account is ready. Now — how would you like to use Kobolog day-to-day?
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {/* WhatsApp */}
          <a
            href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || ""}?text=${encodeURIComponent("Hi Kobolog AI! I just signed up — let's set up my shop 🛍️")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white border-2 border-kudi-green/15 rounded-3xl p-7 hover:border-kudi-green hover:shadow-lg transition-all flex flex-col group"
            data-testid="welcome-wa-cta"
          >
            <div className="w-12 h-12 rounded-2xl bg-kudi-green text-white flex items-center justify-center text-2xl shrink-0">
              <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.057 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884z"/></svg>
            </div>
            <h2 className="font-heading text-xl font-bold mt-5">Use it on WhatsApp</h2>
            <p className="text-sm text-kudi-ink/65 mt-1.5 flex-1">
              Log sales by text, voice or photo. Ask &quot;who owes me?&quot;. Get Daily Diary &amp; reports. <strong>Most traders pick this.</strong>
            </p>
            <div className="mt-5 inline-flex items-center gap-1.5 text-kudi-green font-semibold text-sm group-hover:gap-2.5 transition-all">
              Open WhatsApp <ArrowRight className="w-4 h-4" />
            </div>
          </a>

          {/* Web */}
          <Link
            to="/app/chat"
            className="bg-white border-2 border-border rounded-3xl p-7 hover:border-kudi-terracotta hover:shadow-lg transition-all flex flex-col group"
            data-testid="welcome-web-cta"
          >
            <div className="w-12 h-12 rounded-2xl bg-kudi-terracotta text-white flex items-center justify-center text-2xl shrink-0">🌐</div>
            <h2 className="font-heading text-xl font-bold mt-5">Continue on Web</h2>
            <p className="text-sm text-kudi-ink/65 mt-1.5 flex-1">
              See charts, accept loans, manage your DVA, view debtors, edit profile. The advanced view.
            </p>
            <div className="mt-5 inline-flex items-center gap-1.5 text-kudi-terracotta font-semibold text-sm group-hover:gap-2.5 transition-all">
              Open dashboard <ArrowRight className="w-4 h-4" />
            </div>
          </Link>
        </div>

        <p className="text-center text-xs text-kudi-ink/50 mt-8">
          You can switch between them anytime — both feed the same backend. Log a sale in WhatsApp → see it on Web instantly.
        </p>
      </div>
    </div>
  );
}
